import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Target,
  Loader2,
  Check,
  X,
  Tag,
  Clock,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format relative time until expiration
const formatTimeRemaining = (expiresAt) => {
  const expires = new Date(expiresAt);
  const now = new Date();
  const diffMs = expires - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} remaining`;
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} remaining`;
  }
  return 'Expires soon';
};

const FocusModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState(null);
  const [summary, setSummary] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Fetch current focus
  const fetchFocus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ctx = response.data.context;
      setContext(ctx);
      if (ctx) {
        setSummary(ctx.summary || '');
        setTagsInput((ctx.focus_tags || []).join(', '));
      } else {
        setSummary('');
        setTagsInput('');
      }
    } catch (error) {
      console.error('Failed to fetch focus:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFocus();
      setIsEditing(false);
    }
  }, [open]);

  // Save focus
  const saveFocus = async () => {
    if (!summary.trim()) {
      toast.error('Please enter what you\'re working on');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const tags = tagsInput.split(',').map(t => t.trim()).filter(t => t);
      
      await axios.post(
        `${API_URL}/api/session`,
        { summary: summary.trim(), focus_tags: tags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Focus set successfully');
      await fetchFocus();
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set focus');
    } finally {
      setSaving(false);
    }
  };

  // Clear focus
  const clearFocus = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContext(null);
      setSummary('');
      setTagsInput('');
      toast.success('Focus cleared');
    } catch (error) {
      toast.error('Failed to clear focus');
    } finally {
      setSaving(false);
    }
  };

  // Extend TTL
  const extendFocus = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/session/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Focus extended by 7 days');
      await fetchFocus();
    } catch (error) {
      toast.error('Failed to extend focus');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="focus-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-orange-400" />
            Current Focus
          </DialogTitle>
          <DialogDescription>
            Tell Arlo what you&apos;re working on for more relevant assistance
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : context && !isEditing ? (
          /* Display Current Focus */
          <div className="space-y-4 py-2">
            <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <p className="text-sm leading-relaxed">{context.summary}</p>
              
              {context.focus_tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {context.focus_tags.map((tag, i) => (
                    <span 
                      key={i}
                      className="px-2 py-0.5 bg-orange-500/20 text-orange-500 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              {context.expires_at && (
                <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {formatTimeRemaining(context.expires_at)}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="flex-1"
              >
                Edit Focus
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={extendFocus}
                className="gap-1"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Extend
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFocus}
                disabled={saving}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          /* Edit/Create Focus */
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">What are you working on?</label>
              <Textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="e.g., Building Arlo's email integration feature. Need to set up webhooks and design the UI."
                className="min-h-[100px] resize-none"
                maxLength={500}
                data-testid="focus-summary-input"
              />
              <p className="text-xs text-muted-foreground text-right">
                {summary.length}/500
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                Focus tags (optional)
              </label>
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g., product, development, design"
                data-testid="focus-tags-input"
              />
              <p className="text-xs text-muted-foreground">
                Separate tags with commas
              </p>
            </div>

            {context && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setSummary(context.summary || '');
                  setTagsInput((context.focus_tags || []).join(', '));
                }}
                className="w-full"
              >
                Cancel editing
              </Button>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {(!context || isEditing) && (
            <Button
              onClick={saveFocus}
              disabled={!summary.trim() || saving}
              className="gap-2"
              data-testid="save-focus-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Set Focus
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FocusModal;
